import { motion, useReducedMotion } from 'framer-motion'
import * as React from 'react'

import { cn } from '@/lib/utils'

export interface StepperStep {
  label: React.ReactNode
  description?: React.ReactNode
  disabled?: boolean
  invalid?: boolean
  status?: 'complete' | 'error'
}

export interface StepperProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  steps: StepperStep[]
  active?: number
  orientation?: 'horizontal' | 'vertical'
  onStepChange?: (index: number, step: StepperStep) => void
}

type StepState = 'complete' | 'active' | 'upcoming' | 'error'

const stepSpring = { type: 'spring' as const, stiffness: 320, damping: 32, mass: 0.9 }

function StepCircle({
  state,
  index,
  reduceMotion,
}: {
  state: StepState
  index: number
  reduceMotion: boolean
}) {
  return (
    <motion.span
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-full border-2 text-sm font-semibold',
        'transition-[background-color,border-color,color,box-shadow] duration-200 motion-reduce:transition-none',
        state === 'complete' &&
          'border-brand-blue bg-brand-blue text-brand-black shadow-[0_0_0_4px_color-mix(in_oklch,var(--color-brand-blue)_16%,transparent)]',
        state === 'active' &&
          'border-brand-orange bg-card text-foreground shadow-[0_0_0_4px_color-mix(in_oklch,var(--color-brand-orange)_18%,transparent)]',
        state === 'upcoming' && 'border-border bg-card text-muted-foreground',
        state === 'error' &&
          'border-destructive bg-card text-destructive shadow-[0_0_0_4px_color-mix(in_oklch,var(--destructive)_16%,transparent)]'
      )}
      initial={false}
      animate={{ scale: state === 'active' || state === 'error' ? 1.04 : 1 }}
      transition={reduceMotion ? { duration: 0 } : stepSpring}
      data-slot="step-circle"
    >
      {state === 'error' ? (
        <motion.svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          className="size-4"
          aria-hidden="true"
          initial={reduceMotion ? false : { opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <path d="M12 6v7" />
          <path d="M12 17h.01" />
        </motion.svg>
      ) : state === 'complete' ? (
        <motion.svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-4"
          aria-hidden="true"
        >
          <motion.path
            d="M5 13l4 4L19 7"
            initial={reduceMotion ? false : { pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: reduceMotion ? 0 : 0.3, ease: 'easeOut' }}
          />
        </motion.svg>
      ) : (
        index + 1
      )}
    </motion.span>
  )
}

function Connector({
  filled,
  orientation,
  reduceMotion,
}: {
  filled: boolean
  orientation: 'horizontal' | 'vertical'
  reduceMotion: boolean
}) {
  const horizontal = orientation === 'horizontal'

  return (
    <span
      className={cn(
        'relative overflow-hidden bg-border',
        horizontal ? 'mt-[17px] h-0.5 min-w-8 flex-1' : 'my-1 min-h-6 w-0.5 flex-1'
      )}
      aria-hidden="true"
      data-filled={filled ? 'true' : 'false'}
      data-orientation={orientation}
      data-slot="step-connector"
    >
      <motion.span
        className={cn(
          'absolute inset-0 bg-gradient-to-b from-brand-orange to-brand-blue',
          horizontal && 'bg-gradient-to-r'
        )}
        initial={false}
        animate={horizontal ? { scaleX: filled ? 1 : 0 } : { scaleY: filled ? 1 : 0 }}
        style={{ transformOrigin: horizontal ? 'left' : 'top' }}
        transition={reduceMotion ? { duration: 0 } : stepSpring}
      />
    </span>
  )
}

function StepLabel({ step, state }: { step: StepperStep; state: StepState }) {
  return (
    <span className="block min-w-0">
      <span
        className={cn(
          'block text-sm font-medium leading-tight',
          state === 'upcoming' && 'text-muted-foreground',
          state === 'error' && 'text-destructive',
          state !== 'upcoming' && state !== 'error' && 'text-foreground'
        )}
      >
        {step.label}
      </span>
      {step.description ? (
        <span className="mt-1 block text-xs leading-snug text-muted-foreground">
          {step.description}
        </span>
      ) : null}
    </span>
  )
}

function StepAction({
  children,
  index,
  isActive,
  onStepChange,
  orientation,
  state,
  step,
}: {
  children: React.ReactNode
  index: number
  isActive: boolean
  onStepChange?: StepperProps['onStepChange']
  orientation: 'horizontal' | 'vertical'
  state: StepState
  step: StepperStep
}) {
  const className = cn(
    'rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-60',
    state === 'error'
      ? 'hover:[&_[data-slot=step-circle]]:border-destructive'
      : 'hover:[&_[data-slot=step-circle]]:border-brand-purple',
    orientation === 'horizontal'
      ? 'flex w-full flex-col items-center text-center'
      : 'flex w-full gap-4 text-left'
  )

  if (!onStepChange) {
    return <div className={className}>{children}</div>
  }

  return (
    <button
      type="button"
      aria-current={isActive ? 'step' : undefined}
      aria-invalid={state === 'error' ? 'true' : undefined}
      disabled={step.disabled}
      onClick={() => onStepChange(index, step)}
      className={className}
    >
      {children}
    </button>
  )
}

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(function Stepper(
  {
    steps,
    active = 0,
    orientation = 'horizontal',
    onStepChange,
    className,
    ...props
  },
  ref
) {
  const reduceMotion = Boolean(useReducedMotion())
  const activeIndex = Math.min(Math.max(active, 0), Math.max(steps.length - 1, 0))
  const horizontal = orientation === 'horizontal'

  function stateFor(index: number): StepState {
    const step = steps[index]
    if (step?.status === 'error' || step?.invalid) return 'error'
    if (index === activeIndex) return 'active'
    if (step?.status === 'complete') return 'complete'
    return index < activeIndex ? 'complete' : 'upcoming'
  }

  return (
    <div
      ref={ref}
      role="list"
      className={cn(
        'relative flex w-full',
        horizontal ? 'items-start' : 'flex-col',
        className
      )}
      data-orientation={orientation}
      data-slot="stepper"
      {...props}
    >
      {steps.map((step, index) => {
        const state = stateFor(index)
        const isActive = index === activeIndex
        const isLast = index === steps.length - 1

        return (
          <React.Fragment key={index}>
            <div
              role="listitem"
              className={cn(horizontal && 'w-32 shrink-0')}
              aria-current={isActive ? 'step' : undefined}
              aria-invalid={state === 'error' ? 'true' : undefined}
              data-active={isActive ? 'true' : 'false'}
              data-state={state}
            >
              <StepAction
                index={index}
                isActive={isActive}
                onStepChange={onStepChange}
                orientation={orientation}
                state={state}
                step={step}
              >
                {horizontal ? (
                  <>
                    <StepCircle state={state} index={index} reduceMotion={reduceMotion} />
                    <motion.span
                      className="mt-2 block w-full px-1"
                      initial={false}
                      animate={{ opacity: isActive ? 1 : 0.82 }}
                    >
                      <StepLabel step={step} state={state} />
                    </motion.span>
                  </>
                ) : (
                  <>
                    <span className="flex self-stretch flex-col items-center">
                      <StepCircle state={state} index={index} reduceMotion={reduceMotion} />
                      {!isLast ? (
                        <Connector
                          filled={state === 'complete'}
                          orientation="vertical"
                          reduceMotion={reduceMotion}
                        />
                      ) : null}
                    </span>
                    <span className="pb-7 pt-1">
                      <StepLabel step={step} state={state} />
                    </span>
                  </>
                )}
              </StepAction>
            </div>
            {horizontal && !isLast ? (
              <Connector
                filled={state === 'complete'}
                orientation="horizontal"
                reduceMotion={reduceMotion}
              />
            ) : null}
          </React.Fragment>
        )
      })}
    </div>
  )
})

Stepper.displayName = 'Stepper'

export { Stepper }
